import React, { useMemo } from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	isSearchFederatedEmailEntity,
	type SearchFederatedEntity,
	type SearchResultFederated,
} from '@atlassian/search-client';

import { ProductKeys, useProductConfigs } from '../../../../../../common/constants/products';
import { ConnectorSources } from '../../../../../../common/constants/schemas/3p-config';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';

import { DefaultFederatedResult } from './default-federated-result';
import { EmailSearchResult } from './email-search-result';

interface ThirdPartySearchResultProps {
	partial: SearchResultFederated<SearchFederatedEntity>;
	/** The position in the results list shown to the user. */
	position?: number;
}

const FederatedIcon = ({
	resourceOwner,
	resourceType,
}: {
	resourceOwner?: string;
	resourceType?: string;
}) => {
	const products = useProductConfigs();
	let productKey;
	if (resourceOwner === 'google' && resourceType === 'email') {
		productKey = ProductKeys.Gmail;
	} else if (resourceOwner === 'microsoft' && resourceType === 'email') {
		productKey = ProductKeys.OutlookMail;
	}

	if (!productKey) {
		return <LinkIcon color="currentColor" spacing="spacious" label="" />;
	}

	let IconComponent = products[productKey]?.SearchResultIcon;
	return IconComponent ? (
		<IconComponent label="" />
	) : (
		<LinkIcon color="currentColor" spacing="spacious" label="" />
	);
};

export const ThirdPartyFederatedSearchResult = ({
	partial,
	position,
}: ThirdPartySearchResultProps): React.JSX.Element => {
	const resultAri = AnyAri.parse(partial.id);
	const icon = FederatedIcon(resultAri);

	const metadata = useMemo(() => {
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: resultAri.resourceType,
			entityType: resultAri.resourceType,
			contentId: resultAri.resourceId,
			product: resultAri.resourceOwner,
			// it's federated so always false in this component
			is3PSmartLink: false,
			connectorType: ConnectorSources.Federated,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [resultAri, partial.id]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	if (isSearchFederatedEmailEntity(partial)) {
		return (
			<EmailSearchResult
				partial={partial as SearchResultFederated<SearchFederatedEntity>}
				position={position}
				metadata={metadata}
				icon={icon}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				isThirdParty={true}
			/>
		);
	}

	return (
		<DefaultFederatedResult
			partial={partial}
			position={position}
			metadata={metadata}
			icon={icon}
			onResultClicked={onResultClicked}
			onResultCtxMenuClicked={onResultCtxMenuClicked}
		/>
	);
};
